import { flowApi } from '.';
import { TAG_TYPES } from 'types/services';
import type {
  CheckViewUsageReq,
  CheckViewUsageRes,
  CreateViewReq,
  CreateViewRes,
  DeleteViewsReq,
  DeleteViewsRes,
  GetAllViewsForUserReq,
  GetAllViewsForUserRes,
  GetAllViewsReq,
  GetAllViewsRes,
  GetViewReq,
  GetViewRes,
  GetUsableViewReq,
  GetViewByTypeReq,
  UpdateViewParametersReq,
  UpdateViewParametersRes,
  GetViewRolesReq,
  GetViewRolesRes,
  UpdateViewReq,
  AssignViewRolesReq,
  AssignViewRolesRes,
  RemoveViewRolesReq,
  GetViewsNonParametrizedReq,
  GetViewByTypeRes,
} from 'types';
import type { flowDetailRoleSectionDataScheme } from 'utils/table/admin/flow/roles';

export const viewsService = flowApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    // Currently unused
    deleteViews: builder.mutation<DeleteViewsRes, DeleteViewsReq>({
      query: (data) => ({
        url: 'Parents/Views',
        method: 'DELETE',
        body: data,
      }),
      invalidatesTags: [
        {
          type: TAG_TYPES.VIEWS,
          id: 'LIST',
        },
      ],
    }),

    getAllViewsForUser: builder.query<GetAllViewsForUserRes, GetAllViewsForUserReq>({
      query: ({ parentId }) => ({
        url: `Parents/${parentId}/User/Views`,
        method: 'GET',
      }),
      providesTags: [
        {
          type: TAG_TYPES.VIEWS,
          id: 'LIST',
        },
      ],
    }),

    getAllViews: builder.query<GetAllViewsRes, GetAllViewsReq>({
      query: ({ parentId, viewParentType, ExcludeHiddenViews }) => ({
        url: `Parents/${parentId}/Views`,
        method: 'GET',
        params: {
          ViewParentType: viewParentType,
          ExcludeHiddenViews,
        },
      }),
      providesTags: [
        {
          type: TAG_TYPES.VIEWS,
          id: 'LIST',
        },
      ],
    }),

    checkViewUsage: builder.query<CheckViewUsageRes, CheckViewUsageReq>({
      query: ({ parentId, viewParentType, parameters }) => ({
        url: `Parents/${parentId}/Views/Parameters`,
        method: 'GET',
        params: {
          ViewParentType: viewParentType,
          Parameters: parameters,
        },
      }),
    }),

    getUsableView: builder.query<GetViewRes, GetUsableViewReq>({
      query: ({ parentId, viewId, viewParentType, nestedDataLevels }) => ({
        url: `Parents/${parentId}/Views/${viewId}/Usable`,
        method: 'GET',
        params: {
          ViewParentType: viewParentType,
          NestedDataLevels: nestedDataLevels,
        },
      }),
      providesTags: [
        {
          type: TAG_TYPES.VIEW,
          id: 'LIST',
        },
      ],
    }),

    getUserView: builder.query<GetViewRes, GetViewReq>({
      query: ({ parentId, viewId, viewParentType }) => ({
        url: `Parents/${parentId}/Views/${viewId}/Me`,
        method: 'GET',
        params: {
          ViewParentType: viewParentType,
        },
      }),
      providesTags: [
        {
          type: TAG_TYPES.VIEW,
          id: 'LIST',
        },
      ],
    }),

    getAdminView: builder.query<GetViewRes, GetViewReq>({
      query: ({ parentId, viewId, viewParentType }) => ({
        url: `Parents/${parentId}/Views/${viewId}/Admin`,
        method: 'GET',
        params: {
          ViewParentType: viewParentType,
        },
      }),
      providesTags: [
        {
          type: TAG_TYPES.VIEW,
          id: 'LIST',
        },
      ],
    }),

    getViewByType: builder.query<GetViewByTypeRes, GetViewByTypeReq>({
      query: ({ viewType }) => ({
        url: `Views/ByType/${viewType}`,
        method: 'GET',
      }),
      providesTags: [
        {
          type: TAG_TYPES.VIEW,
          id: 'VIEW_BY_TYPE',
        },
      ],
    }),

    createOrUpdateView: builder.mutation<CreateViewRes, CreateViewReq>({
      query: ({
        viewName,
        viewType,
        viewParentType,
        viewParameters,
        parentId,
        viewId,
        orderDesc,
        defaultOrderByItemId,
        displayNestedListColumns,
        shoppingCartParameters,
        calendarParameters,
      }) => ({
        url: `Parents/${parentId}/Views`,
        method: 'PUT',
        body: {
          viewName,
          viewType,
          viewParentType,
          viewParameters,
          viewId,
          orderDesc,
          defaultOrderByItemId,
          displayNestedListColumns,
          shoppingCartParameters,
          calendarParameters,
        },
      }),
      invalidatesTags: [
        {
          type: TAG_TYPES.VIEWS,
          id: 'LIST',
        },
        {
          type: TAG_TYPES.VIEW,
          id: 'LIST',
        },
        {
          type: TAG_TYPES.COLUMNS,
          id: 'LIST',
        },
      ],
    }),
    // Currently unused
    updateView: builder.mutation<CreateViewRes, UpdateViewReq>({
      query: ({ viewId, viewName, viewParentType, viewParameters, parentId }) => ({
        url: `Parents/${parentId}/Views`,
        method: 'PUT',
        body: {
          viewId,
          viewName,
          viewParentType,
          viewParameters,
        },
      }),
      invalidatesTags: [
        {
          type: TAG_TYPES.VIEWS,
          id: 'LIST',
        },
        {
          type: TAG_TYPES.VIEW,
          id: 'LIST',
        },
      ],
    }),

    deleteView: builder.mutation<
      void,
      {
        parentId: string;
        viewName: string;
        viewParentType: 'Table' | 'Agenda';
      }
    >({
      query: ({ parentId, viewName, viewParentType }) => ({
        url: `Parents/${parentId}/Views/${viewName}`,
        method: 'DELETE',
        body: {
          viewParentType,
        },
      }),
      invalidatesTags: [
        {
          type: TAG_TYPES.VIEWS,
          id: 'LIST',
        },
        {
          type: TAG_TYPES.VIEW,
          id: 'LIST',
        },
      ],
    }),
    // Currently unused
    updateViewParameters: builder.mutation<UpdateViewParametersRes, UpdateViewParametersReq>({
      query: ({ parentId, viewName, viewParentType, visibilityState }) => ({
        url: `Parents/${parentId}/Views/${viewName}/VisibilityState`,
        method: 'PUT',
        params: {
          ViewParentType: viewParentType,
          VisibilityState: visibilityState,
        },
      }),
      invalidatesTags: [
        {
          type: TAG_TYPES.VIEWS,
          id: 'LIST',
        },
        {
          type: TAG_TYPES.VIEW,
          id: 'LIST',
        },
      ],
    }),

    updateViewVisibilityState: builder.mutation<
      unknown,
      {
        parentId: string;
        viewId: string;
        viewParentType: 'Table' | 'Agenda';
        visibilityState: 'Visible' | 'Hidden';
      }
    >({
      query: ({ parentId, viewId, viewParentType, visibilityState }) => ({
        url: `Parents/${parentId}/Views/${viewId}/VisibilityState`,
        method: 'PUT',
        body: {
          viewParentType,
          visibilityState,
        },
      }),
      invalidatesTags: [
        {
          type: TAG_TYPES.VIEWS,
          id: 'LIST',
        },
        {
          type: TAG_TYPES.VIEW,
          id: 'LIST',
        },
      ],
    }),

    getViewsNonParametrized: builder.query<GetAllViewsRes, GetViewsNonParametrizedReq>({
      query: ({ parentId, viewParentType }) => ({
        url: `Parents/${parentId}/Views/NonParametrized`,
        method: 'GET',
        params: {
          ViewParentType: viewParentType,
        },
      }),
      providesTags: [
        {
          type: TAG_TYPES.VIEWS,
          id: 'LIST',
        },
      ],
    }),

    getViewRoles: builder.query<GetViewRolesRes, GetViewRolesReq<typeof flowDetailRoleSectionDataScheme>>({
      query: (data) => ({
        url: `ViewRoles/Agendas/${data.agendaId}/Views/${data.viewId}/Roles/WithUsers`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: {
          roleIds: null,
        },
        params: {
          Page: data.page,
          PageSize: data.pageSize,
          OrderBy: data.orderBy,
          OrderDesc: data.orderDesc,
          Search: data.search,
        },
      }),

      providesTags: [{ type: TAG_TYPES.VIEW, id: 'ROLES' }],
    }),

    assignViewRoles: builder.mutation<AssignViewRolesRes, AssignViewRolesReq>({
      query: ({ agendaId, viewId, data }) => ({
        url: `ViewRoles/Agendas/${agendaId}/Views/${viewId}/Roles`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: data,
      }),
      invalidatesTags: [{ type: TAG_TYPES.VIEW, id: 'ROLES' }],
    }),

    removeViewRole: builder.mutation<unknown, RemoveViewRolesReq>({
      query: ({ agendaId, viewId, roleId }) => ({
        url: `ViewRoles/Agendas/${agendaId}/Views/${viewId}/Roles/${roleId}`,
        method: 'DELETE',
      }),

      invalidatesTags: [{ type: TAG_TYPES.VIEW, id: 'ROLES' }],
    }),
  }),
});
export const {
  useCheckViewUsageQuery,
  useCreateOrUpdateViewMutation,
  useDeleteViewMutation,
  useDeleteViewsMutation,
  useGetAllViewsForUserQuery,
  useGetAllViewsQuery,
  useGetUsableViewQuery,
  useGetUserViewQuery,
  useGetAdminViewQuery,
  useGetViewByTypeQuery,
  useLazyCheckViewUsageQuery,
  useLazyGetAllViewsForUserQuery,
  useLazyGetAllViewsQuery,
  useLazyGetUsableViewQuery,
  useLazyGetUserViewQuery,
  useLazyGetAdminViewQuery,
  useUpdateViewMutation,
  useUpdateViewParametersMutation,
  useUpdateViewVisibilityStateMutation,
  useGetViewRolesQuery,
  useAssignViewRolesMutation,
  useRemoveViewRoleMutation,
  useGetViewsNonParametrizedQuery,
} = viewsService;
